import i18n from '../i18n';
import API from '../api';

/** Full product catalog — languages shipped in this package. */
export const UI_LANGUAGES = ['en', 'de', 'fr', 'nl'];

const LABELS = { en: 'EN', de: 'DE', fr: 'FR', nl: 'NL' };

/** Runtime academy allowlist (public regional settings). */
let academyEnabled = UI_LANGUAGES.slice();
let academyDefault = 'en';
let allowlistReady = false;

export function getUiLanguageLabel(code) {
  return LABELS[code] || String(code || '').toUpperCase();
}

export function getAcademyEnabledUiLanguages() {
  return academyEnabled.slice();
}

export function getAcademyDefaultUiLanguage() {
  return academyDefault;
}

export function isAcademyUiLanguageAllowlistReady() {
  return allowlistReady;
}

/**
 * Sync allowlist from public regional settings.
 * Always keeps English; clamps current i18n language if needed.
 */
export function syncAcademyUiLanguages({ enabledUiLanguages, defaultUiLanguage } = {}) {
  const parsed = normalizeEnabledList(enabledUiLanguages);
  academyEnabled = parsed.length ? parsed : ['en'];
  academyDefault = clampToAllowlist(defaultUiLanguage, academyEnabled[0] || 'en');
  allowlistReady = true;
  const current = clampToAllowlist(
    i18n.language || localStorage.getItem('uiLanguage') || academyDefault,
    academyDefault,
  );
  if ((i18n.language || '').slice(0, 2) !== current) {
    applyUiLanguage(current);
  } else {
    localStorage.setItem('uiLanguage', current);
  }
  return { enabled: academyEnabled.slice(), defaultLanguage: academyDefault };
}

function normalizeEnabledList(raw) {
  const out = [];
  const seen = new Set();
  const push = (code) => {
    const c = String(code || '').trim().toLowerCase().slice(0, 2);
    if (!UI_LANGUAGES.includes(c) || seen.has(c)) return;
    seen.add(c);
    out.push(c);
  };
  push('en');
  if (Array.isArray(raw)) {
    raw.forEach(push);
  } else if (typeof raw === 'string') {
    raw.split(/[,\s]+/).forEach(push);
  }
  return out;
}

function clampToAllowlist(raw, fallback = 'en') {
  const code = String(raw || '').trim().toLowerCase().slice(0, 2);
  const pool = allowlistReady ? academyEnabled : UI_LANGUAGES;
  if (UI_LANGUAGES.includes(code) && pool.includes(code)) return code;
  const fb = String(fallback || 'en').trim().toLowerCase().slice(0, 2);
  if (pool.includes(fb)) return fb;
  return pool[0] || 'en';
}

/** Normalize to a supported 2-letter UI language code within the academy allowlist. */
export function normalizeUiLanguage(raw, fallback) {
  return clampToAllowlist(raw, fallback || academyDefault || 'en');
}

/** Apply language to i18n + localStorage (syncUiLanguage runs via languageChanged). */
export function applyUiLanguage(raw) {
  const lng = normalizeUiLanguage(raw);
  if ((i18n.language || '').slice(0, 2) !== lng) {
    i18n.changeLanguage(lng);
  }
  localStorage.setItem('uiLanguage', lng);
  return lng;
}

/** Persist preferred language for the signed-in user (Header / profile). */
export async function persistPreferredLanguage(raw) {
  const lng = normalizeUiLanguage(raw);
  await API.put('/me/preferred-language', { preferredLanguage: lng });
  return lng;
}

/** Serialize allowlist for regional settings API (CSV). */
export function serializeEnabledUiLanguages(list) {
  return normalizeEnabledList(list).join(',');
}
